import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { storeContactMessage } from '../db/firebaseContact';

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    });
    const [loading, setLoading] = useState(false);
    const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

    const contactInfo = [
        {
            icon: <Phone className="w-6 h-6" />,
            title: "Phone",
            details: "+92 333 3112200",
            description: "Mon to Fri 9am to 6pm"
        },
        {
            icon: <Mail className="w-6 h-6" />,
            title: "Email",
            details: "help@leatherhood.pk",
            description: "Send us your query anytime!"
        },
        {
            icon: <MapPin className="w-6 h-6" />,
            title: "Address",
            details: "Rawalpindi, Pakistan",
            description: "Pakistan"
        },
        {
            icon: <Clock className="w-6 h-6" />,
            title: "Working Hours",
            details: "Monday - Saturday",
            description: "9:00 AM - 6:00 PM"
        }
    ];

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setSubmitStatus({ type: '', message: '' });

        try {
            // Basic validation
            if (!formData.name || !formData.email || !formData.message) {
                setSubmitStatus({
                    type: 'error',
                    message: 'Please fill in all required fields.'
                });
                setLoading(false);
                return;
            }

            if (!/\S+@\S+\.\S+/.test(formData.email)) {
                setSubmitStatus({
                    type: 'error',
                    message: 'Please enter a valid email address.'
                });
                setLoading(false);
                return;
            }

            // Store in Firebase
            const submissionId = await storeContactMessage(formData);
            
            setSubmitStatus({
                type: 'success',
                message: 'Thank you for your message! We will get back to you within 24 hours.'
            });

            // Reset form
            setFormData({
                name: '',
                email: '',
                phone: '',
                subject: '',
                message: ''
            });

        } catch (error) {
            console.error('Error submitting form:', error);
            setSubmitStatus({
                type: 'error',
                message: 'Sorry, there was an error sending your message. Please try again.'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-beige py-12">
            <div className="max-w-6xl mx-auto px-4">
                ""
                {/* Header */}
                <div className="text-center mb-16">
                    <h1 className="text-4xl md:text-5xl font-bold text-charcoal mb-4">
                        Get In Touch
                    </h1>
                    <p className="text-xl text-darktaupe max-w-2xl mx-auto">
                        Have questions about our leather products? We're here to help. 
                        Reach out to us and we'll respond as soon as possible.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                    {/* Contact Information */}
                    <div className="lg:col-span-1">
                        <div className="bg-offwhite rounded-2xl p-8 shadow-sm border border-taupe/20">
                            <h2 className="text-2xl font-bold text-charcoal mb-6">Contact Information</h2>
                            <p className="text-darktaupe mb-8">
                                Fill out the form or contact us through any of these channels. 
                                We're always happy to assist you with your leather needs.
                            </p>

                            <div className="space-y-6">
                                {contactInfo.map((item, index) => (
                                    <div key={index} className="flex items-start space-x-4">
                                        <div className="bg-primary rounded-full p-3 text-offwhite flex-shrink-0">
                                            {item.icon}
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-charcoal text-lg">{item.title}</h3>
                                            <p className="text-darktaupe font-medium">{item.details}</p>
                                            <p className="text-taupe text-sm">{item.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Social Media Links */}
                            <div className="mt-8 pt-8 border-t border-taupe/20">
                                <h3 className="font-semibold text-charcoal mb-4">Follow Us</h3>
                                <div className="flex space-x-4">
                                    {['Facebook', 'Instagram', 'Twitter'].map((social) => (
                                        <button
                                            key={social}
                                            className="bg-taupe text-offwhite px-4 py-2 rounded-lg hover:bg-primary transition-colors text-sm"
                                        >
                                            {social}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Contact Form */}
                    <div className="lg:col-span-2">
                        <div className="bg-offwhite rounded-2xl p-8 shadow-sm border border-taupe/20">
                            <h2 className="text-2xl font-bold text-charcoal mb-2">Send us a Message</h2>
                            <p className="text-darktaupe mb-6">
                                Fill out the form below and we'll get back to you within 24 hours.
                            </p>

                            {/* Status Message */}
                            {submitStatus.message && (
                                <div className={`p-4 rounded-lg mb-6 flex items-center space-x-3 ${
                                    submitStatus.type === 'success' 
                                        ? 'bg-green-100 text-green-800 border border-green-200' 
                                        : 'bg-red-100 text-red-800 border border-red-200'
                                }`}>
                                    {submitStatus.type === 'success' ? (
                                        <CheckCircle className="w-5 h-5" />
                                    ) : (
                                        <AlertCircle className="w-5 h-5" />
                                    )}
                                    <span>{submitStatus.message}</span>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label htmlFor="name" className="block text-sm font-medium text-charcoal mb-2">
                                            Full Name *
                                        </label>
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-3 border border-taupe/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                                            placeholder="Enter your full name"
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="email" className="block text-sm font-medium text-charcoal mb-2">
                                            Email Address *
                                        </label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-3 border border-taupe/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                                            placeholder="your@email.com"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label htmlFor="phone" className="block text-sm font-medium text-charcoal mb-2">
                                            Phone Number
                                        </label>
                                        <input
                                            type="tel"
                                            id="phone"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 border border-taupe/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                                            placeholder="+92 300 1234567"
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="subject" className="block text-sm font-medium text-charcoal mb-2">
                                            Subject
                                        </label>
                                        <select
                                            id="subject"
                                            name="subject"
                                            value={formData.subject}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 border border-taupe/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                                        >
                                            <option value="">Select a subject</option>
                                            <option value="product-inquiry">Product Inquiry</option>
                                            <option value="order-support">Order Support</option>
                                            <option value="wholesale">Wholesale Inquiry</option>
                                            <option value="custom-order">Custom Order</option>
                                            <option value="complaint">Complaint</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="message" className="block text-sm font-medium text-charcoal mb-2">
                                        Message *
                                    </label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        required
                                        rows="6"
                                        className="w-full px-4 py-3 border border-taupe/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors resize-none"
                                        placeholder="Tell us about your inquiry, question, or feedback..."
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className={`w-full py-4 px-6 rounded-lg font-semibold text-lg transition-colors flex items-center justify-center space-x-3 ${
                                        loading
                                            ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                                            : 'bg-primary text-offwhite hover:bg-charcoal'
                                    }`}
                                >
                                    {loading ? (
                                        <>
                                            <div className="w-5 h-5 border-2 border-offwhite border-t-transparent rounded-full animate-spin" />
                                            <span>Sending...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Send className="w-5 h-5" />
                                            <span>Send Message</span>
                                        </>
                                    )}
                                </button>

                                <p className="text-sm text-taupe text-center">
                                    * Required fields. We respect your privacy and will never share your information.
                                </p>
                            </form>
                        </div>
                    </div>
                </div>

                {/* FAQ Section */}
                <div className="mt-16 bg-offwhite rounded-2xl p-8 shadow-sm border border-taupe/20">
                    <h2 className="text-2xl font-bold text-charcoal mb-6 text-center">Frequently Asked Questions</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                            <h3 className="font-semibold text-charcoal mb-2">Do you offer international shipping?</h3>
                            <p className="text-darktaupe">Currently, we only ship within Pakistan. We're working on expanding our shipping options.</p>
                        </div>
                        <div>
                            <h3 className="font-semibold text-charcoal mb-2">What is your return policy?</h3>
                            <p className="text-darktaupe">We offer 7-day returns for unused items in original condition. Custom orders are final sale.</p>
                        </div>
                        <div>
                            <h3 className="font-semibold text-charcoal mb-2">How do I care for my leather product?</h3>
                            <p className="text-darktaupe">We provide care instructions with every purchase. Generally, avoid water and direct sunlight.</p>
                        </div>
                        <div>
                            <h3 className="font-semibold text-charcoal mb-2">Do you do custom orders?</h3>
                            <p className="text-darktaupe">Yes! We specialize in custom leather goods. Contact us with your requirements.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Contact;